import { useCallback, useEffect, useState } from 'react'
import { getRecoveryAnchorRepository } from '@/repository'
import { useRepoVersion } from '@/repository/repoVersionStore'
import { useRemoteRefreshStore } from '@/store/remoteRefreshStore'
import { totalOptimizedBalance, computeRecoveryProgress } from '@/engine/recovery'
import { useDebts } from '@/hooks/useDebts'
import type { RecoveryAnchor, Debt } from '@/types'

/**
 * Read/write seam for the user's recovery anchor.
 *
 * **Auto-init contract.** On first mount with no persisted anchor and a
 * non-empty registry, the hook seeds an anchor from today's date + the
 * current optimized total. This means a user who upgrades from a pre-v0.7.8
 * build doesn't see "Recovery: no anchor" — they see "Recovery: 0% (started
 * today)" with an obvious affordance to set their real start date and peak
 * debt. No surprises, no dead state.
 *
 * The auto-init runs at most once per session even if debts change during
 * the session — `setAnchor` only fires inside the effect when the *stored*
 * anchor is null, never when the user has explicitly set one.
 *
 * Subscribed components also get the resolved `progress` object computed
 * against current optimized debts so they don't have to import the engine.
 */
export function useRecoveryAnchor() {
  const { debts, isInitialized: debtsReady } = useDebts()
  const [anchor, setAnchorState] = useState<RecoveryAnchor | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  // v0.8.24: subscribe to repoVersion so `bindReposToSession` flips
  // (sign-in / sign-out) re-trigger the load below against the now-different
  // backing repo. Pre-fix the effect had `[]` deps and the Dashboard's
  // Recovery Progress headline rendered the local anchor for an entire
  // post-sign-in session.
  const repoVersion = useRepoVersion()
  // v0.8.57: also refetch on background refreshes so an anchor set by the
  // other household member shows up without a reload.
  const remoteTick = useRemoteRefreshStore((s) => s.tick)

  // Initial load from repo. Re-fires when the repo binding changes
  // (`repoVersion` bumps inside `bindReposToSession.resetStores()`).
  // The stale anchor renders for at most one frame between binding flip
  // and the new fetch's resolution — acceptable given the alternative is
  // a synchronous setState-in-effect (lint forbids; see react-hooks/set-state-in-effect).
  useEffect(() => {
    let cancelled = false
    void getRecoveryAnchorRepository()
      .get()
      .then((stored) => {
        if (!cancelled) {
          setAnchorState(stored)
          setIsLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [repoVersion, remoteTick])

  // Auto-init exactly once per session if there's no anchor but there ARE
  // debts to anchor against. Race-safe via the explicit guard on `isLoaded`
  // and `anchor === null`.
  useEffect(() => {
    if (!isLoaded || !debtsReady) return
    if (anchor !== null) return
    if (debts.length === 0) return // nothing to anchor; show empty state instead

    const total = totalOptimizedBalance(debts)
    if (total <= 0) return // user has only excluded/zero-balance debts

    const seeded: RecoveryAnchor = {
      startDate: todayISODate(),
      startingTotal: total,
      setAt: new Date().toISOString(),
    }
    void getRecoveryAnchorRepository()
      .set(seeded)
      .then(() => setAnchorState(seeded))
  }, [isLoaded, debtsReady, anchor, debts])

  /** Replace the anchor with new values. Persists, then updates state. */
  const setAnchor = useCallback(async (next: { startDate: string; startingTotal: number }) => {
    const updated: RecoveryAnchor = {
      startDate: next.startDate,
      startingTotal: next.startingTotal,
      setAt: new Date().toISOString(),
    }
    await getRecoveryAnchorRepository().set(updated)
    setAnchorState(updated)
  }, [])

  /** Force-clear the anchor. Triggers the auto-init path on next render. */
  const clearAnchor = useCallback(async () => {
    await getRecoveryAnchorRepository().clear()
    setAnchorState(null)
  }, [])

  const progress = computeRecoveryProgress(anchor, debts)

  return {
    anchor,
    progress,
    isLoaded,
    setAnchor,
    clearAnchor,
  }
}

/** Today as "YYYY-MM-DD" in local time. Avoids UTC drift on date displays. */
function todayISODate(): string {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Re-export for components that want to compute progress against arbitrary debts. */
export type { RecoveryAnchor, Debt }
